import Form from '@rjsf/core';
import {
  BaseMetadataDocument,
  Content,
  DocumentVersionIdentifier,
  createDocument,
  deleteDocument,
} from '@sgdocs/client';
import { DsButtonToggle, DsContextualHelp, DsSgDatePicker } from '@sgdocs/design-system';
import { validate } from 'jsonschema';
import { useMemo, useRef, useState } from 'react';
import { QueryClientProvider } from 'react-query';
import { Spinner } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from '../common/hooks/queryClient';
import { useQueryGetModel } from '../common/hooks/useQueryGetModel';
import {
  JsonSchemaWidgets,
  computeSchema,
  computeUiSchema,
  flattenFormData,
} from '../common/json-schema/JsonSchemaForm';
import { EWidgetsSgmDocs } from '../common/widget/enum';
import { openHelpCenter } from '../common/widget/help-center';
import { DocumentsFiles, SGMDocsUpload } from '../sgm-docs-upload/SGMDocsUpload';

const defaultLabels = {
  commonCancel: 'Cancel',
  commonCreate: 'Create',
  commonNo: 'No',
  commonYes: 'Yes',
  documentFilename: 'Filename(s)',
  documentMetadata: 'Metadata',
  documentModeMulti: 'Several documents',
  documentModeMultiDescription: 'Create a new document for each single file',
  documentModeMultiDescriptionDisabled:
    'Create one document for all files. This option is disabled when having files with the same extension.',
  documentModeSingle: 'Single document',
  documentModeSingleDescription: 'Create one document for all files',
  documentModeSingleDescriptionDisabled:
    'Create one document for all files. This option is disabled when there is no several files.',
  documentModeTitle: 'Creation mode',
  documentModelName: 'Model',
  documentProperties: 'Properties',
  documentPropertiesConfidentialityLevel: 'Confidentiality',
  documentPropertiesContentOutsideSgNetwork: 'Outside SG network',
  documentPropertiesDestroyDate: 'Destroy date',
  documentWorkspaceName: 'Workspace',
  modelFetchError: 'An error occurred while trying to retrieve model info',
  use_search: 'use search values',
  use_default: 'use default values',
};

const computeFormData = (documentData: any, schema: any): any => {
  const formDataTemp = { ...documentData };
  for (const key in documentData) {
    if (typeof documentData[key] === 'string') {
      if (schema.properties[key]?.type === 'array') {
        formDataTemp[key] = [documentData[key]];
      }
      if (schema.properties[key]?.type === 'boolean') {
        formDataTemp[key] = Boolean(JSON.parse(documentData[key]));
      }
    }
  }
  return formDataTemp;
};

interface ICreateDocumentFormComponentProps {
  bodyFormClass?: string;
  documentFormData: any;
  filesToUpload?: File[];
  onCancel: () => void;
  labels?: any;
  locale?: string;
  modelId: string;
  onCreateCompleted: (documentVersionIdentifiers: DocumentVersionIdentifier[]) => void;
  submitFormClass?: string;
  token?: string | null;
  workspaceId: string;
  workspaceName: string;
  sgmDocsBaseUrl: string;
  fileChunkMinLength: number;
  hideProperties?: boolean;
}

const CreateDocumentFormComponent = ({
  sgmDocsBaseUrl,
  fileChunkMinLength,
  token = null,
  filesToUpload = [],
  workspaceName,
  modelId,
  workspaceId,
  documentFormData,
  onCancel,
  onCreateCompleted,
  bodyFormClass,
  submitFormClass,
  locale = 'en',
  labels,
  hideProperties = false,
}: ICreateDocumentFormComponentProps): JSX.Element => {
  const [documentConfidentialityLevel, setDocumentConfidentialityLevel] = useState<string>();
  const [documentDestroyDate, setDocumentDestroyDate] = useState<string>('');
  const [documentContentsOutsideSGNetwork, setDocumentContentsOutsideSGNetwork] = useState<boolean>();
  const [localFormData, setLocalFormData] = useState({});
  const [applyFilters, setApplyFilters] = useState<boolean>(true);
  const uploadRef = useRef<SGMDocsUpload>(null);
  const [remainDocumentsIdToUpload, setRemainDocumentsIdToUpload] = useState<string[]>([]);
  const [extraErrors, setExtraErrors] = useState<any>({});
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [documentsCreated, setDocumentsCreated] = useState<string[]>([]);
  const [documentsFailed, setDocumentsFailed] = useState<string[]>([]);
  const {
    data: model,
    isLoading,
    isError,
  } = useQueryGetModel(
    { workspaceId, modelId },
    { token, sgmDocsBaseUrl, component: EWidgetsSgmDocs.CREATE_DOCUMENT_FORM }
  );
  const [modeMulti, setModeMulti] = useState<boolean>(true);
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentVersionIdentifier[]>([]);

  const labelsIntl = useMemo(
    () => ({
      ...defaultLabels,
      ...labels,
    }),
    [labels]
  );

  const filenames = useMemo(
    () => (filesToUpload?.length > 0 ? filesToUpload.map((file) => file.name || '').filter(Boolean) : []),
    [filesToUpload]
  );

  if (isError || !model) {
    return <div className="m-2">{labelsIntl.modelFetchError}</div>;
  }

  if (isLoading) {
    return (
      <div className="text-center m-2">
        <Spinner color="primary" />
      </div>
    );
  }

  const schema = computeSchema(model?.jsonSchema);
  const uiSchema = computeUiSchema(schema, 2);
  const formData = computeFormData(documentFormData, schema);

  if (applyFilters && Object.keys(localFormData).length === 0 && Object.keys(formData).length !== 0) {
    setLocalFormData({ ...formData });
  }

  let destroyDatePlaceholder: string | undefined = undefined;
  const offsetMatches = /P(\d+Y)?(\d+M)?(\d+D)?/.exec(model?.defaultExpiry || '');
  if (offsetMatches) {
    const defaultDestroyDate = new Date();
    defaultDestroyDate.setFullYear(
      defaultDestroyDate.getFullYear() +
      (offsetMatches[1] !== undefined ? parseInt(offsetMatches[1].replace('Y', '')) : 0)
    );
    defaultDestroyDate.setMonth(
      defaultDestroyDate.getMonth() + (offsetMatches[2] !== undefined ? parseInt(offsetMatches[2].replace('M', '')) : 0)
    );
    defaultDestroyDate.setDate(
      defaultDestroyDate.getDate() + (offsetMatches[3] !== undefined ? parseInt(offsetMatches[3].replace('D', '')) : 0)
    );
    const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(defaultDestroyDate);
    const month = new Intl.DateTimeFormat(locale, { month: '2-digit' }).format(defaultDestroyDate);
    const day = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(defaultDestroyDate);

    destroyDatePlaceholder = `${day}/${month}/${year}`;
  }

  const createNewDocument = async (newDocument: any, jsonSchema: any): Promise<boolean> => {
    if (!model) {
      return false;
    }
    const formattedFormData = flattenFormData(newDocument.schema, newDocument.formData);
    const result = validate(formattedFormData, jsonSchema, { base: 'http://localhost' });
    const errors = result.errors;
    if (errors && errors.length > 0) {
      const newErrors: any = {};
      errors.forEach((error: any) => {
        const message =
          error.name === 'required' ? error.message : `Please check ${error.argument} definition in JSON schema`;
        console.error(error);
        newErrors[error.argument] = {
          __errors: [message],
        };
      });
      setExtraErrors(newErrors);
      setIsCreating(false);
      return false;
    } else {
      const tokenUsed: string =
        token ?? (window.SGWTWidgetConfiguration.bus.dangerouslyGetCurrentValue('sg-connect.access-token') || '');

      setExtraErrors({});
      let documents: BaseMetadataDocument[];
      const documentDraft = {
        workspaceId: model?.workspaceId,
        modelId: model?.id,
        confidentialityLevel: documentConfidentialityLevel,
        destroyDate: documentDestroyDate,
        contentsOutsideSGNetwork: documentContentsOutsideSGNetwork,
        metadata: formattedFormData,
        authorizationHeader: tokenUsed,
        baseUrl: sgmDocsBaseUrl,
        component: EWidgetsSgmDocs.CREATE_DOCUMENT_FORM,
      };
      if (modeMulti) {
        documents = await Promise.all(
          filesToUpload.map(() => {
            return createDocument(documentDraft);
          })
        );
      } else {
        documents = [await createDocument(documentDraft)];
      }
      setDocumentsCreated([...documents.map((document) => document.documentVersionIdentifier.documentId)]);

      if (documents.length > 0) {
        const documentsIdToUpload: string[] = documents.map((document) => {
          return document.documentVersionIdentifier.documentId;
        });
        const documentIdToUpload = documentsIdToUpload.pop();
        uploadFile(documentIdToUpload || '');
        setRemainDocumentsIdToUpload(documentsIdToUpload);
        return true;
      } else {
        setIsCreating(false);
        console.error(documents);
        return false;
      }
    }
  };

  const hasDuplicateContentType = (filesToUpload: File[] | undefined): boolean => {
    if (!filesToUpload || filesToUpload.length <= 1) {
      return false;
    }
    const types = new Set(filesToUpload?.map((file) => file.type));
    const extensions = new Set(
      filesToUpload?.map((file) => {
        const getExtension = file.name.split('.');
        return getExtension.length > 0 ? getExtension.pop() : '';
      })
    );
    return types.size != filesToUpload.length || extensions.size != filesToUpload.length;
  };
  const forceMultiMode = hasDuplicateContentType(filesToUpload);
  const onUploadFailed = ({ documentId }: { documentId: string }): void => {
    setDocumentsFailed((prev) => [...prev, documentId]);
    setIsCreating(false);
  };

  const onClose = async (): Promise<void> => {
    documentsFailed.forEach((id) =>
      deleteDocument({
        workspaceId: workspaceId,
        documentId: id,
        authorizationHeader: token,
        baseUrl: sgmDocsBaseUrl,
        component: EWidgetsSgmDocs.CREATE_DOCUMENT_FORM,
      })
    );
    onCancel();
  };

  const onAbort = async (): Promise<void> => {
    documentsCreated.forEach((id) => {
      deleteDocument({
        workspaceId: workspaceId,
        documentId: id,
        authorizationHeader: token,
        baseUrl: sgmDocsBaseUrl,
        component: EWidgetsSgmDocs.CREATE_DOCUMENT_FORM,
      });
    });
    onCancel();
  };

  const handleApplyFilters = (apply: boolean): void => {
    if (apply) {
      setLocalFormData({ ...formData });
    } else {
      setLocalFormData({});
    }
    setApplyFilters(apply);
  };

  const onUploadCompleted = (contentUploaded: Content): void => {
    const documentId = contentUploaded.documentVersionIdentifier.documentId;
    // Remove the documentId from documentsFailed if it exists
    setDocumentsFailed((prev) => prev.filter((id) => id !== documentId));

    if (modeMulti) {
      if (remainDocumentsIdToUpload && remainDocumentsIdToUpload.length === 0) {
        return onCreateCompleted([...uploadedDocuments, contentUploaded]);
      } else {
        setUploadedDocuments((prevState: DocumentVersionIdentifier[]) => [...prevState, contentUploaded]);
        const documentIdToUpload: string | undefined = remainDocumentsIdToUpload.pop();
        if (documentIdToUpload) {
          uploadFile(documentIdToUpload);
        }
        setRemainDocumentsIdToUpload([...remainDocumentsIdToUpload]);
      }
    } else if (contentUploaded.documentVersionIdentifier) {
      if (filesToUpload && filesToUpload.length === 0) {
        const documentVersionIdentifier = contentUploaded.documentVersionIdentifier;
        onCreateCompleted([{ documentVersionIdentifier: documentVersionIdentifier }]);
      } else {
        uploadFile(contentUploaded.documentVersionIdentifier.documentId);
      }
    } else {
      setIsCreating(false);
      console.error(contentUploaded);
    }
  };

  const uploadFile = (documentId: string): void => {
    const content: File | undefined = filesToUpload?.pop() || undefined;
    if (content && uploadRef.current && model) {
      const documentsFiles: DocumentsFiles = { [model.workspaceId]: { [documentId]: [{ file: content }] } };
      uploadRef.current.upload(documentsFiles);
    }
  };

  const submitForm = (): void => {
    setIsCreating(true);
    document.getElementById('add-document-button')?.click();
  };


  return (
    <>
      <div className={bodyFormClass}>
        <div className="form-group mb-0">
          <label>{labelsIntl.documentFilename}</label>
        </div>
        <div className="d-flex flex-column">
          {filenames?.length > 0 &&
            filenames.map((filename) => (
              <label className="mb-1" key={filename}>
                {filename}
              </label>
            ))}
        </div>
        <div className="form-group field field-string">
          <DsButtonToggle
            id="multi-mode"
            label={<>{labelsIntl.documentModeTitle}</>}
            items={[
              {
                value: 'true',
                label: labelsIntl.documentModeMulti,
                tooltip:
                  filesToUpload?.length <= 1
                    ? labelsIntl.documentModeMultiDescriptionDisabled
                    : labelsIntl.documentModeMultiDescription,
                iconLabel: `filter_${filesToUpload?.length <= 9 ? filesToUpload?.length : '9_plus'}`,
                disable: filesToUpload?.length <= 1,
                iconPosition: 'right',
              },
              {
                value: 'false',
                iconPosition: 'right',
                iconLabel: 'looks_one_outline',
                iconOutlined: true,
                label: labelsIntl.documentModeSingle,
                tooltip: forceMultiMode
                  ? labelsIntl.documentModeSingleDescriptionDisabled
                  : labelsIntl.documentModeSingleDescription,

                disable: forceMultiMode,
              },
            ]}
            defaultValue={(filesToUpload?.length > 1).toString()}
            onChange={(value): void => setModeMulti(value === 'true')}
          />
        </div>
        <div className="form-group field field-string">
          <label>{labelsIntl.documentWorkspaceName}</label>
          <p>{workspaceName}</p>
        </div>
        <div className="form-group field field-string">
          <label>{labelsIntl.documentModelName}</label>
          <p>{model.name}</p>
        </div>
        {!hideProperties && (
          <div className="mb-4">
            <h4>{labelsIntl.documentProperties}</h4>
            <div className="px-0">
              <div className="form-group field field-object">
                <fieldset id="add-properties">
                  <div className="form-group field field-string col-md-6">
                    <DsButtonToggle
                      id="confidentiality-level"
                      label={<>{labelsIntl.documentPropertiesConfidentialityLevel}</>}
                      items={[
                        {
                          label: 'C0',
                          value: 'C0',
                          disable:
                            model.useAip ||
                            model.minimumConfidentialityLevel === 'C1' ||
                            model.minimumConfidentialityLevel === 'C2' ||
                            model.minimumConfidentialityLevel === 'C3',
                        },
                        {
                          label: 'C1',
                          value: 'C1',
                          disable:
                            model.useAip ||
                            model.minimumConfidentialityLevel === 'C2' ||
                            model.minimumConfidentialityLevel === 'C3',
                        },
                        {
                          label: 'C2',
                          value: 'C2',
                          disable: model.useAip || model.minimumConfidentialityLevel === 'C3',
                        },
                        {
                          label: 'C3',
                          value: 'C3',
                          disable: true,
                        },
                      ]}
                      defaultValue={model.defaultConfidentialityLevel}
                      onChange={setDocumentConfidentialityLevel}
                    />
                  </div>
                  <div className="form-group field field-string col-md-6">
                    <DsButtonToggle
                      id="content-outside-sg-network"
                      label={<>{labelsIntl.documentPropertiesContentOutsideSgNetwork}</>}
                      items={[
                        {
                          label: labelsIntl.commonYes,
                          value: 'true',
                          disable: !model.allowContentsOutsideSGNetwork,
                        },
                        {
                          label: labelsIntl.commonNo,
                          value: 'false',
                        },
                      ]}
                      defaultValue={model.defaultContentsOutsideSGNetwork ? 'true' : 'false'}
                      onChange={(value: string): void => setDocumentContentsOutsideSGNetwork(value === 'true')}
                    />
                  </div>
                  {!model.wormStorage && (
                    <div className="form-group field field-string col-md-6">
                      <label>{labelsIntl.documentPropertiesDestroyDate}</label>
                      <DsContextualHelp id="top.docs_def_destroy" openContextualHelp={openHelpCenter} />
                      <DsSgDatePicker
                        id="destroy-date"
                        onChange={(date: string | undefined): void =>
                          setDocumentDestroyDate(date ? date.split('T')[0] : '')
                        }
                        placeholder={destroyDatePlaceholder}
                        displayFormat="dd/MM/yyyy"
                        locale={locale}
                      />
                    </div>
                  )}
                </fieldset>
              </div>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <h4>{labelsIntl.documentMetadata}</h4>
          {Object.keys(formData).length !== 0 && (
            <div className="form-check form-switch">
              <label className="form-check-label mb-0 align-self-center" htmlFor="applyFilters">
                {applyFilters ? labelsIntl.use_search : labelsIntl.use_default}
              </label>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="applyFilters"
                onChange={(event): void => handleApplyFilters(event.target.checked)}
                checked={applyFilters}
              />
            </div>
          )}
        </div>
        <div className="px-0 pt-2">
          <Form
            idPrefix="add-document"
            name="add-document-form"
            schema={schema}
            uiSchema={uiSchema}
            widgets={JsonSchemaWidgets}
            formData={localFormData}
            extraErrors={extraErrors}
            onChange={(e): void => setLocalFormData(e.formData)}
            noHtml5Validate
            noValidate
            onSubmit={(newDocument): Promise<boolean> => createNewDocument(newDocument, model.jsonSchema)}
          >
            <button id="add-document-button" type="submit" style={{ display: 'none' }} />
          </Form>
        </div>
      </div>
      <div className={submitFormClass}>
        <div className="d-flex flex-row mt-2">
          <button className="btn btn-lg btn-default me-4" onClick={onAbort}>
            {labelsIntl.commonCancel}
          </button>
          {documentsCreated.length > 0 && filesToUpload.length > 0 && (
            <button className="btn btn-lg btn-info" onClick={submitForm}>
              {isCreating ? <Spinner style={{ width: '1rem', height: '1rem' }} /> : 'Continue'}
            </button>
          )}
          {!documentsCreated.length && filesToUpload.length > 0 && (
            <button className="btn btn-lg btn-info" onClick={submitForm}>
              {isCreating ? <Spinner style={{ width: '1rem', height: '1rem' }} /> : 'Create'}
            </button>
          )}
          {!filesToUpload.length && !documentsFailed.length && (
            <button className="btn btn-lg btn-primary me-4" onClick={onCancel}>
              Close
            </button>
          )}
          {!filesToUpload.length && documentsFailed.length > 0 && (
            <button className="btn btn-lg btn-primary me-4" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
      <SGMDocsUpload
        ref={uploadRef}
        sgdocsBaseUrl={sgmDocsBaseUrl}
        fileChunkMinLength={fileChunkMinLength}
        onUploadCompleted={onUploadCompleted}
        onUploadFailed={onUploadFailed}
        enableDeletion
      />
    </>
  );
};

export const SGMDocsCreateDocumentForm = ({
  sgmDocsBaseUrl,
  fileChunkMinLength,
  token,
  filesToUpload,
  workspaceName,
  modelId,
  workspaceId,
  documentFormData,
  onCancel,
  onCreateCompleted,
  bodyFormClass,
  submitFormClass,
  locale,
  labels,
  hideProperties,
}: ICreateDocumentFormComponentProps): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <CreateDocumentFormComponent
        key={uuidv4()}
        sgmDocsBaseUrl={sgmDocsBaseUrl}
        fileChunkMinLength={fileChunkMinLength}
        token={token}
        filesToUpload={filesToUpload}
        workspaceName={workspaceName}
        modelId={modelId}
        workspaceId={workspaceId}
        documentFormData={documentFormData}
        onCancel={onCancel}
        onCreateCompleted={onCreateCompleted}
        bodyFormClass={bodyFormClass}
        submitFormClass={submitFormClass}
        locale={locale}
        labels={labels}
        hideProperties={hideProperties}
      />
    </QueryClientProvider>
  );
};
